export type DistributionSummaryDetail = {
  label: string;
  value: number;
  percentage?: number;
};

export type TimeSeriesSummaryPoint = {
  label: string;
  value: number;
};

export type AlignmentSummaryPoint = {
  label: string;
  consumers: number;
  sources: number;
};

function formatValue(value: number, unit?: string): string {
  if (!unit) {
    return `${value}`;
  }

  return `${value}${unit}`;
}

export function buildDistributionSummary(params: {
  chartName: string;
  details: DistributionSummaryDetail[];
}): string {
  const { chartName, details } = params;

  if (details.length === 0) {
    return `${chartName}. No data available.`;
  }

  const total = details.reduce((acc, detail) => acc + detail.value, 0);
  const topCategory = details.reduce((top, detail) =>
    detail.value > top.value ? detail : top,
  );

  const breakdown = details
    .map(detail => {
      const percentageText =
        detail.percentage !== undefined ? ` (${detail.percentage}%)` : '';
      return `${detail.label}: ${detail.value}${percentageText}`;
    })
    .join(', ');

  return `${chartName}. Total value is ${total}. Largest category is ${topCategory.label} with ${topCategory.value}. Breakdown: ${breakdown}.`;
}

export function buildTimeSeriesSummary(params: {
  chartName: string;
  data: TimeSeriesSummaryPoint[];
  unit?: string;
}): string {
  const { chartName, data, unit } = params;

  if (data.length === 0) {
    return `${chartName}. No data available.`;
  }

  const firstPoint = data[0];
  const lastPoint = data[data.length - 1];
  const maxPoint = data.reduce((max, point) =>
    point.value > max.value ? point : max,
  );
  const minPoint = data.reduce((min, point) =>
    point.value < min.value ? point : min,
  );

  let trend = 'stable';
  if (lastPoint.value > firstPoint.value) {
    trend = 'increasing';
  } else if (lastPoint.value < firstPoint.value) {
    trend = 'decreasing';
  }

  return `${chartName}. Time range from ${firstPoint.label} to ${
    lastPoint.label
  }. Trend is ${trend}, moving from ${formatValue(
    firstPoint.value,
    unit,
  )} to ${formatValue(lastPoint.value, unit)}. Peak is ${formatValue(
    maxPoint.value,
    unit,
  )} in ${maxPoint.label}. Lowest value is ${formatValue(
    minPoint.value,
    unit,
  )} in ${minPoint.label}.`;
}

export function buildAlignmentSummary(params: {
  chartName: string;
  data: AlignmentSummaryPoint[];
  consumersLabel: string;
  sourcesLabel: string;
}): string {
  const { chartName, data, consumersLabel, sourcesLabel } = params;

  if (data.length === 0) {
    return `${chartName}. No data available.`;
  }

  const totals = data.reduce(
    (acc, point) => {
      return {
        consumers: acc.consumers + point.consumers,
        sources: acc.sources + point.sources,
      };
    },
    { consumers: 0, sources: 0 },
  );

  const highestMonth = data.reduce((highest, point) => {
    const currentTotal = point.consumers + point.sources;
    const highestTotal = highest.consumers + highest.sources;
    return currentTotal > highestTotal ? point : highest;
  });

  return `${chartName}. Total ${consumersLabel} is ${
    totals.consumers
  } and total ${sourcesLabel} is ${
    totals.sources
  }. Highest combined value is in ${highestMonth.label} with ${
    highestMonth.consumers + highestMonth.sources
  }.`;
}
